import {
  AuthenticatedUserProvider,
  createBrowserRouterWithSentry,
  EnvironmentProvider,
  GoogleMapsProvider,
  NetworkStatusProvider,
} from '@airship-platform/airship-patterns';
import { TinyMCEProvider } from '@airship-platform/jam-web/components-x';
import { platformTheme } from '@airship-platform/jam-web/theme/platform';
import { version } from 'package.json';
import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { RouterProvider, ScrollRestoration } from 'react-router';
import App from './app/App';
import routes from './app/App.routes';
import { OfflineDbProvider } from './app/data-offline';
import { createRxDbConnectionService } from './app/data-offline/rxdb/utils/rxDbConnectionService';
import { createRxDbDataService } from './app/data-offline/rxdb/utils/rxDbDataService';
import { setupRxDbTelemetry } from './app/data-offline/rxdb/utils/rxDbTelemetry';
import { PwaUpdatePrompt } from './app/features/offline-diagnostics';

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
const signInUrl = import.meta.env.VITE_SIGN_IN_URL;
const mode = import.meta.env.VITE_MODE;
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const basePublicFilesUrl = import.meta.env.VITE_PUBLIC_FILES_BUCKET;
const amplitudeApiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
const tinyMCEApiKey = import.meta.env.VITE_TINYMCE_API_KEY;
const isAmplitudeEnabled =
  !import.meta.env.VITE_ENABLE_AMPLITUDE ||
  import.meta.env.VITE_ENABLE_AMPLITUDE === 'true';
const gtmContainerId = import.meta.env.VITE_GTM_CONTAINER_ID;
const isGTMEnabled = import.meta.env.VITE_GTM_ENABLED === 'true';

const rxDbConnectionService = createRxDbConnectionService();
const rxDbDataService = createRxDbDataService(rxDbConnectionService);
setupRxDbTelemetry(rxDbConnectionService);

const rootComponent = document.getElementById('root') as HTMLElement;
rootComponent.setAttribute('data-app-version', version);
const root = ReactDOM.createRoot(rootComponent);

const buildRoutes = createBrowserRouterWithSentry(
  [
    {
      element: (
        <EnvironmentProvider
          env={mode}
          apiBaseUrl={apiBaseUrl}
          theme={platformTheme}
          authConfig={{
            signInUrl,
            apiBaseUrl,
            basePublicFilesUrl,
            callbackUrl: '/service-management/callback',
          }}
          assetsConfig={{ basePublicFilesUrl }}
          sentryConfig={{ sentryDsn, mode, version }}
          amplitudeConfig={{
            apiKey: amplitudeApiKey,
            enableAmplitude: isAmplitudeEnabled,
            version,
          }}
          gtmConfig={{
            containerIds: [gtmContainerId],
            enableGTM: isGTMEnabled,
          }}
        >
          <NetworkStatusProvider>
            <AuthenticatedUserProvider>
              <GoogleMapsProvider apiKey={googleMapsApiKey}>
                <TinyMCEProvider apiKey={tinyMCEApiKey}>
                  <PwaUpdatePrompt />
                  <OfflineDbProvider
                    connection={rxDbConnectionService}
                    data={rxDbDataService}
                  >
                    <App />
                    <ScrollRestoration
                      getKey={(location) => location.pathname}
                    />
                  </OfflineDbProvider>
                </TinyMCEProvider>
              </GoogleMapsProvider>
            </AuthenticatedUserProvider>
          </NetworkStatusProvider>
        </EnvironmentProvider>
      ),
      children: routes,
    },
  ],
  { basename: '/service-management' }
);

root.render(
  <StrictMode>
    <RouterProvider router={buildRoutes()} />
  </StrictMode>
);
